/**
 * ResetPasswordForm - Reset password form with modern Dashboard3 styling
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Heading, Text } from "@/components/ui";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useAuthResetPassword, useTenantData } from "@/hooks";
import { type ResetPasswordFormSchema, resetPasswordSchema } from "@/schemas";
import NextLink from "next/link";
import { PageTypePortal } from "@/lib/types";
import { useToast } from "@/components/feedback";
import { Link } from "@/components/ui";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { ContentSettingsText } from "@/lib/utils/content-settings/ContentSettingsText";
import { HiOutlineArrowLeft } from "react-icons/hi2";

export type ResetPasswordFormProps = {
  loginPath?: string;
  backToSignInPath?: string;
};

export const ResetPasswordForm = ({
  loginPath = PageTypePortal.Login,
  backToSignInPath,
}: ResetPasswordFormProps = {}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const searchParams = useSearchParams();
  const uid = searchParams.get("uid") || "";

  const token = useMemo(() => {
    const raw = searchParams.get("token") ?? "";
    return raw.replace(/ /g, "+");
  }, [searchParams]);

  const router = useRouter();
  const toast = useToast();
  const theme = useThemeTokens();
  const { tenantId, tenantData } = useTenantData();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const form = useForm<ResetPasswordFormSchema>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
  });

  const { register, handleSubmit, formState } = form;
  const { errors, isValid } = formState;

  const { mutate, isPending, isSuccess, isError } = useAuthResetPassword({
    toast,
    uid,
    token,
  });

  const onSubmit = useCallback(
    (data: ResetPasswordFormSchema) => {
      setHasSubmitted(true);
      mutate({
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });
    },
    [mutate],
  );

  const handleGoToLogin = useCallback(() => {
    router.push(loginPath);
  }, [router, loginPath]);

  const showSuccessView = useMemo(
    () => hasSubmitted && isSuccess && !isError,
    [hasSubmitted, isSuccess, isError],
  );

  if (showSuccessView) {
    return (
      <Flex direction="column" w="100%" gap={6}>
        <Box>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.18em"
            color={theme.text.tertiary}
            mb={2}
          >
            {contentSettings.resetPasswordSuccessLabel}
          </Text>
          <Heading size="xl" fontWeight="600" letterSpacing="-0.03em" mb={3}>
            {contentSettings.resetPasswordSuccessTitle}
          </Heading>
          <ContentSettingsText
            variant="secondary"
            fontSize="sm"
            lineHeight="1.6"
            tenantEmail={tenantData?.email}
          >
            {contentSettings.resetPasswordSuccessDescription}
          </ContentSettingsText>
        </Box>

        <Button
          h="44px"
          w="100%"
          fontSize="sm"
          variant="primary"
          fontWeight="500"
          onClick={handleGoToLogin}
        >
          {contentSettings.resetPasswordSuccessButtonText}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%" gap={6}>
      {backToSignInPath && (
        <Flex justify="flex-start">
          <Link
            as={NextLink}
            href={backToSignInPath}
            fontSize="sm"
            fontWeight="500"
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            sx={{
              "@media (hover: hover)": {
                "&:hover": { textDecoration: "underline" },
              },
            }}
          >
            <HiOutlineArrowLeft size={16} /> Back to sign in
          </Link>
        </Flex>
      )}
      <Box>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.18em"
          color={theme.text.tertiary}
          mb={2}
        >
          {contentSettings.resetPasswordLabel}
        </Text>
        <Heading size="xl" fontWeight="600" letterSpacing="-0.03em" mb={3}>
          {contentSettings.resetPasswordTitle}
        </Heading>
        <ContentSettingsText
          variant="secondary"
          fontSize="sm"
          lineHeight="1.6"
          tenantEmail={tenantData?.email}
        >
          {contentSettings.resetPasswordDescription}
        </ContentSettingsText>
      </Box>

      <form style={{ width: "100%" }} onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={4}>
          <LabeledInput
            name="newPassword"
            label="New Password"
            errors={errors}
            register={register}
            type="password"
            placeholder="Enter new password"
            required
          />

          <LabeledInput
            name="confirmPassword"
            label="Confirm Password"
            errors={errors}
            register={register}
            type="password"
            placeholder="Confirm new password"
            required
          />

          <Button
            h="44px"
            w="100%"
            type="submit"
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            isLoading={isPending}
            loadingText="Resetting..."
            isDisabled={!isValid || isPending}
          >
            Reset Password
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};
